/**
 * Map element types for z-index management
 * Determines the stacking order of elements on the map
 */
export const MapElement = {
  CONTROL_ZONE: 0,
  LAUNCHPAD: 1,
  DELIVERY_POINT: 2,
  PICKUP_POINT: 3,
  DOCK_POINT: 4,
  VEHICLE: 5,
  MISSION_ROUTE: 6,
  MISSION_ROUTE_LOCKED_SEGMENT: 7,
  ROUTE_TO_SAFE_POINT: 8,
  SAFE_POINT: 9,
  FOREIGN_AIRCRAFT: 10
} as const;

export type MapElement = (typeof MapElement)[keyof typeof MapElement];

/**
 * Get the z-index for a specific map element
 * Lower values appear behind, higher values appear in front
 *
 * @param element - The map element type
 * @returns The z-index value for that element
 */
export function getMapElementZIndex(element: MapElement): number {
  switch (element) {
    // These elements are on the ground
    case MapElement.CONTROL_ZONE:
    case MapElement.SAFE_POINT:
    case MapElement.DELIVERY_POINT:
    case MapElement.PICKUP_POINT:
    case MapElement.DOCK_POINT:
    case MapElement.LAUNCHPAD:
      return 10;
    case MapElement.MISSION_ROUTE:
    case MapElement.ROUTE_TO_SAFE_POINT:
      return 20;
    case MapElement.MISSION_ROUTE_LOCKED_SEGMENT:
      return 30;
    case MapElement.VEHICLE:
    case MapElement.FOREIGN_AIRCRAFT:
      return 40;
    default:
      throw new Error(`Unknown MapElement type: ${element}`);
  }
}

/**
 * Get a dynamic z-index for airborne map elements based on altitude.
 * Intended for VEHICLE and FOREIGN_AIRCRAFT.
 *
 * @param element - The map element type
 * @param altitude - Altitude in meters
 * @returns The dynamic z-index value
 */
export function getDynamicMapElementZIndex(element: MapElement, altitude: number): number {
  const baseZIndex = getMapElementZIndex(element);

  if (element !== MapElement.VEHICLE && element !== MapElement.FOREIGN_AIRCRAFT) {
    throw new Error(
      `Dynamic z-index is only applicable for VEHICLE and FOREIGN_AIRCRAFT elements. Received: ${element}`
    );
  }

  const altitudeOffset = Math.max(0, Math.floor(altitude));
  return baseZIndex + altitudeOffset;
}
